export const SRP_PAGE_SEGMENT = 'page';

export type QueryValue = string | Array<string> | undefined;

export function parseSrpPageNumber(value: QueryValue): number | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return null;
  if (!/^\d+$/.test(raw)) return null;

  const page = Number(raw);
  return Number.isSafeInteger(page) && page >= 1 ? page : null;
}

export function getSrpPageFromQuery(value: QueryValue): number {
  return parseSrpPageNumber(value) ?? 1;
}

export function getSrpPathPage(pathname: string): number | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 4) return null;

  const pageSegment = segments[segments.length - 2];
  if (pageSegment !== SRP_PAGE_SEGMENT) return null;

  return parseSrpPageNumber(segments[segments.length - 1]);
}

export function stripSrpPageSegment(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 4 && segments[segments.length - 2] === SRP_PAGE_SEGMENT) {
    const page = parseSrpPageNumber(segments[segments.length - 1]);
    if (page !== null) {
      const base = `/${segments.slice(0, -2).join('/')}`;
      return base === '/' ? '/' : base;
    }
  }

  return pathname || '/';
}

export function buildSrpPagePath(basePathname: string, page: number): string {
  const base = stripSrpPageSegment(basePathname).replace(/\/+$/, '') || '/';
  if (page <= 1) return base;
  return `${base}/${SRP_PAGE_SEGMENT}/${page}`;
}

export function appendQuery(pathname: string, query: Record<string, QueryValue> = {}): string {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (key === SRP_PAGE_SEGMENT || value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
    } else if (value !== '') {
      params.append(key, value);
    }
  });

  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

export function buildSrpPageHref(
  basePathname: string,
  page: number,
  query: Record<string, QueryValue> = {},
): string {
  return appendQuery(buildSrpPagePath(basePathname, page), query);
}

export function resolveSrpPage({
  pathPage,
  queryPage,
}: {
  pathPage?: QueryValue;
  queryPage?: QueryValue;
}): number {
  return parseSrpPageNumber(pathPage) ?? getSrpPageFromQuery(queryPage);
}

export function getSrpPageFromUrl(
  pathname: string | null | undefined,
  queryPage?: QueryValue,
  fallbackPage = 1,
): number {
  return (pathname ? getSrpPathPage(pathname) : null) ?? parseSrpPageNumber(queryPage) ?? fallbackPage;
}
